import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Share, Compass, PlusSquare, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isIOSSafari, trackEvent } from "@/lib/platform";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where the sheet was opened from (analytics only). */
  source?: string;
}

/**
 * iOS "Add to Home Screen" helper sheet.
 *
 * Safari  → the four-step Share → Add to Home Screen instructions.
 * Non-Safari (Chrome/Google app/Instagram/Facebook webviews) → a short note
 * asking the user to reopen the page in Safari, since Add-to-Home-Screen is
 * unavailable (or broken) there. iOS has no `beforeinstallprompt`, so no
 * programmatic install is ever promised.
 */
export default function IosInstallSheet({ open, onOpenChange, source = "unknown" }: Props) {
  const { t } = useTranslation();
  const safari = isIOSSafari();

  useEffect(() => {
    if (open) trackEvent("ios_install_help_opened", { source, safari });
  }, [open, source, safari]);

  const steps = safari
    ? [
        { icon: Compass, text: t("install.iosSheetStep1") },
        { icon: Share, text: t("install.iosSheetStep2") },
        { icon: PlusSquare, text: t("install.iosSheetStep3") },
        { icon: Check, text: t("install.iosSheetStep4") },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share className="w-5 h-5 text-primary" /> {t("install.iosSheetTitle")}
          </DialogTitle>
          <DialogDescription>
            {safari ? t("install.iosSheetIntro") : t("install.iosNonSafari")}
          </DialogDescription>
        </DialogHeader>

        {safari ? (
          <>
            <ol className="space-y-2.5">
              {steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl border border-border/60 bg-secondary/40 px-3 py-2.5">
                  <span className="flex items-center justify-center w-6 h-6 shrink-0 rounded-full bg-primary/15 text-primary text-xs font-bold">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-snug flex-1">{s.text}</span>
                  <s.icon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden />
                </li>
              ))}
            </ol>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("install.iosReassurance")}
            </p>
          </>
        ) : (
          <div className="rounded-xl border border-border/60 bg-secondary/40 px-3 py-2.5 text-sm leading-snug">
            {t("install.iosNonSafariHint")}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
